import { AlertSettingsItem, DEFAULT_LIMIT, UserUnitSettings } from '../common/constants'
import { apiRequestProtected, apiRequestPublic } from '../utils/axios'
import { compareFetchLimit } from '../utils/concurrencyLimit'
import {
  fetchWindowedRange,
  splitIntoWindows,
  spanExceedsCap,
  noteMaxRangeDaysFromResponse,
} from '../utils/rangeWindowing'

async function getVersion(): Promise<{ version: string; gitHash: string }> {
  return apiRequestPublic('GET', '/version')
}

async function healthcheck(): Promise<any> {
  return apiRequestPublic('GET', '/healthcheck')
}

async function healthcheckProtected(): Promise<any> {
  return apiRequestProtected('GET', '/healthcheck-protected')
}

async function signIn(email: string, password: string): Promise<any> {
  return apiRequestPublic('POST', '/login', {
    email,
    password,
  })
}

async function getDevices(formatted = false, withConfigs = false): Promise<any> {
  return await apiRequestProtected('GET', `/devices?formatted=${formatted}&withConfigs=${withConfigs}`)
}

async function getDevicesPage(
  page: number,
  limit: number,
  formatted = true,
  withConfigs = true
): Promise<{ devices: any[]; total?: number; page?: number; limit?: number; hasMore?: boolean }> {
  return await apiRequestProtected(
    'GET',
    `/devices?formatted=${formatted}&withConfigs=${withConfigs}&page=${page}&limit=${limit}`
  )
}

async function getDeviceConfig(sigfoxId: string, formatted = true): Promise<any> {
  return await apiRequestProtected('GET', `/device-configs?sigfoxId=${sigfoxId}&formatted=${formatted}`)
}

async function getSensorConfig(
  sigfoxId: string,
  timestamp: number = Math.floor(Date.now() / 1000),
  showAllSensors: boolean = false
): Promise<any> {
  return await apiRequestProtected(
    'GET',
    `/sensor-configs?sigfoxId=${sigfoxId}&timestamp=${timestamp}&showAllSensors=${showAllSensors}`
  )
}

async function getRawConfigMessages(
  sigfoxId: string,
  deviceConfig: boolean = true,
  sensorConfig: boolean = true
): Promise<any> {
  return await apiRequestProtected(
    'GET',
    `/raw-messages-configs?sigfoxId=${sigfoxId}&deviceConfig=${deviceConfig}&sensorConfig=${sensorConfig}`
  )
}

async function getUserUnits(): Promise<any> {
  return apiRequestProtected('GET', '/user-unit-settings')
}

async function getUnitConversions(): Promise<any> {
  return apiRequestProtected('GET', '/unit-conversions')
}

async function updateUserUnitSettings(userUnitSettings: UserUnitSettings): Promise<any> {
  return apiRequestProtected('POST', '/user-unit-settings', { userUnitSettings })
}

// Merge multiple /readings-fast window responses into one body of the same
// shape. `data` is keyed by "<sensorPort>-<unitCategoryId>"; per key we union
// the `readings` and `hidden` timestamp maps (later windows overwrite dupes).
function mergeReadingsResponses(parts: any[]): any {
  const valid = parts.filter(Boolean)
  if (valid.length === 0) return { data: {} }
  if (valid.length === 1) return valid[0]

  const mergedData: Record<string, any> = {}
  for (const part of valid) {
    const data = part?.data || {}
    for (const [key, sensor] of Object.entries<any>(data)) {
      if (!mergedData[key]) {
        mergedData[key] = {
          ...sensor,
          readings: { ...(sensor?.readings || {}) },
          hidden: { ...(sensor?.hidden || {}) },
        }
      } else {
        Object.assign(mergedData[key].readings, sensor?.readings || {})
        Object.assign(mergedData[key].hidden, sensor?.hidden || {})
      }
    }
  }
  // Keep the first window's top-level fields (message/sigfoxId/deviceId/paging);
  // only the merged sensor data spans the full requested range.
  return { ...valid[0], data: mergedData }
}

async function getReadings(sigfoxId: string, from: number, to: number, limit: number = DEFAULT_LIMIT): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  // Large ranges are split into <= cap windows and merged (see rangeWindowing).
  return fetchWindowedRange(
    from,
    to,
    (wFrom, wTo) =>
      apiRequestProtected('GET', `/readings-fast?sigfoxId=${sigfoxId}&from=${wFrom}&to=${wTo}&limit=${limit}`),
    mergeReadingsResponses
  )
}

async function hideReadings(sigfoxId: string, from: number, to: number, sensorPort?: string): Promise<any> {
  return apiRequestProtected('POST', `/hide-readings`, { sigfoxId, from, to, ...(sensorPort && { sensorPort }) })
}

async function unhideReadings(sigfoxId: string, from?: number, to?: number, sensorPort?: string): Promise<any> {
  return apiRequestProtected('POST', `/unhide-readings`, {
    sigfoxId,
    ...(from !== undefined && { from }),
    ...(to !== undefined && { to }),
    ...(sensorPort && { sensorPort })
  })
}

// Merge multiple /battery-readings window responses. `data` holds `battery` and
// `power` timestamp maps (with a flat-map fallback), unioned across windows.
function mergeBatteryResponses(parts: any[]): any {
  const valid = parts.filter(Boolean)
  if (valid.length === 0) return { data: { battery: {}, power: {} } }
  if (valid.length === 1) return valid[0]

  const battery: Record<string, any> = {}
  const power: Record<string, any> = {}
  for (const part of valid) {
    const data = part?.data || {}
    Object.assign(battery, data.battery || {})
    Object.assign(power, data.power || {})
  }
  return { ...valid[0], data: { ...(valid[0].data || {}), battery, power } }
}

async function getBatteryReadings(sigfoxId: string, from: number, to: number, limit: number = 5000): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  return fetchWindowedRange(
    from,
    to,
    (wFrom, wTo) =>
      apiRequestProtected('GET', `/battery-readings?sigfoxId=${sigfoxId}&from=${wFrom}&to=${wTo}&limit=${limit}`),
    mergeBatteryResponses
  )
}

// How far back a "latest reading" lookup will search before giving up. Mirrors
// the server's 6-month hard ceiling — a device with no battery reading in this
// window is treated as having none rather than crawling back to the epoch.
const LATEST_BATTERY_LOOKBACK_DAYS = 180

/**
 * Fetch only the single most-recent battery reading for a device.
 *
 * Callers that just want the last value must NOT go through getBatteryReadings
 * with from=0: windowing that span fans out into hundreds of parallel <=cap
 * requests (one per 30-day window across the device's entire history), which
 * floods the API and exhausts the connection pool. Instead we walk windows
 * newest-first and return as soon as one contains a reading — a single request
 * for any recently-reporting device, and at most a handful for one that has
 * been offline. Returns the same `{ data: { battery, power } }` shape.
 */
async function getLatestBatteryReading(
  sigfoxId: string,
  to: number = Math.floor(Date.now() / 1000)
): Promise<any> {
  const from = Math.max(0, to - LATEST_BATTERY_LOOKBACK_DAYS * 24 * 60 * 60)
  const windows = splitIntoWindows(from, to) // most-recent first
  let lastRes: any = null
  for (const window of windows) {
    const res = await compareFetchLimit(() =>
      apiRequestProtected(
        'GET',
        `/battery-readings?sigfoxId=${sigfoxId}&from=${window.from}&to=${window.to}&limit=1`
      )
    )
    noteMaxRangeDaysFromResponse(res)
    lastRes = res
    const battery = res?.data?.battery
    if (battery && typeof battery === 'object' && Object.keys(battery).length > 0) {
      return res
    }
  }
  return lastRes ?? { data: { battery: {}, power: {} } }
}

async function getLastReadings(sigfoxId: string): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  const timestamp = Math.floor(Date.now() / 1000)
  const response = apiRequestProtected('GET', `/readings-fast?sigfoxId=${sigfoxId}&to=${timestamp}&limit=1`)
  return response
}

async function getDevicesStatus(deviceIds: string[], thresholdMinutes: number = 360): Promise<any> {
  if (!deviceIds || deviceIds.length === 0) {
    return { statuses: {}, thresholdMinutes, timestamp: Math.floor(Date.now() / 1000) }
  }

  const ids = deviceIds.join(',')
  return apiRequestProtected('GET', `/devices-status?deviceIds=${ids}&thresholdMinutes=${thresholdMinutes}`)
}

async function getSensorConfigs(sigfoxId: string, timestamp: number, showAllSensors: boolean = false): Promise<any> {
  return apiRequestProtected(
    'GET',
    `/sensor-configs?sigfoxId=${sigfoxId}&timestamp=${timestamp}&showAllSensors=${showAllSensors}`
  )
}

async function getRawSigfoxMessages(
  sigfoxId: string,
  to?: number,
  from?: number,
  order: string = 'asc',
  limit: number = 2500,
  messageTypes?: string[]
): Promise<any> {
  const buildQuery = (wFrom?: number, wTo?: number): string => {
    let queryString = `/raw-messages?sigfoxId=${sigfoxId}&limit=${limit}&order=${order}`
    if (wFrom !== undefined && wFrom !== null) queryString += `&from=${wFrom}`
    if (wTo !== undefined && wTo !== null) queryString += `&to=${wTo}`
    if (messageTypes && messageTypes.length > 0) {
      queryString += `&messageTypes=${messageTypes.join(',')}`
    }
    return queryString
  }
  const fetchWindow = (wFrom?: number, wTo?: number) =>
    compareFetchLimit(() => apiRequestProtected('GET', buildQuery(wFrom, wTo)))

  // Open-ended (server bounds it) or already within the cap: a single request.
  if (from === undefined || from === null || to === undefined || to === null || !spanExceedsCap(from, to)) {
    const res = await fetchWindow(from, to)
    noteMaxRangeDaysFromResponse(res)
    return res
  }

  // Large range: walk <= cap windows in the sort direction and stop as soon as
  // we have `limit` messages. This preserves the caller's cursor-based infinite
  // scroll (it re-invokes us with a narrowed range on "load more"), while never
  // issuing a single request that would exceed the server cap.
  const desc = String(order).toLowerCase() === 'desc'
  let windows = splitIntoWindows(from, to) // most-recent first
  if (!desc) windows = windows.slice().reverse() // ascending -> oldest first

  const collected: any[] = []
  const seen = new Set<any>()
  let lastRes: any = null

  for (const window of windows) {
    const res = await fetchWindow(window.from, window.to)
    noteMaxRangeDaysFromResponse(res)
    lastRes = res
    for (const row of res?.data || []) {
      if (!seen.has(row.MSG_RAW_ID)) {
        seen.add(row.MSG_RAW_ID)
        collected.push(row)
      }
    }
    if (collected.length >= limit) break
  }

  collected.sort((a, b) => {
    const byTime = desc ? b.SIGFOX_TIME - a.SIGFOX_TIME : a.SIGFOX_TIME - b.SIGFOX_TIME
    if (byTime !== 0) return byTime
    return desc ? b.MSG_RAW_ID - a.MSG_RAW_ID : a.MSG_RAW_ID - b.MSG_RAW_ID
  })

  return { ...(lastRes || {}), data: collected.slice(0, limit) }
}

async function getHysteresisSettings(): Promise<any> {
  return apiRequestProtected('GET', '/hysteresis')
}

async function updateHysteresisSettings(hysteresis: number): Promise<any> {
  return apiRequestProtected('POST', '/hysteresis', {
    hysteresis,
  })
}

async function getAlertControls(): Promise<any> {
  return apiRequestProtected('GET', '/alert-controls')
}

async function updateAlertControls(settings: object): Promise<any> {
  return apiRequestProtected('POST', '/alert-controls', { ...settings })
}

async function getAlertSettings(deviceId: number): Promise<any> {
  return apiRequestProtected('GET', '/alert-settings' + (deviceId ? `?deviceId=${deviceId}` : ''))
}

async function createAlertSettings(settings: AlertSettingsItem): Promise<any> {
  return apiRequestProtected('POST', '/alert-settings', { ...settings })
}

async function updateAlertSettings(settings: AlertSettingsItem): Promise<any> {
  return apiRequestProtected('PATCH', '/alert-settings', { ...settings })
}

async function getSensorPosition(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-position?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function getSensorThresholds(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-thresholds?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function setSensorThresholds(deviceId: number, sensorId: number, data: object): Promise<any> {
  return apiRequestProtected('POST', `/sensor-thresholds`, {
    deviceId,
    sensorId,
    ...data,
  })
}

async function getSensorAlerts(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-alerts?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function setSensorAlerts(deviceId: number, sensorId: number, data: object): Promise<any> {
  return apiRequestProtected('POST', `/sensor-alerts`, {
    deviceId,
    sensorId,
    ...data,
  })
}

async function getAlertHistorySummary(): Promise<any> {
  return apiRequestProtected('GET', '/alert-history/summary')
}

async function getDeviceAlertStates(): Promise<any> {
  return apiRequestProtected('GET', '/device-alert-state')
}

async function getAlertHistory(params: { deviceId?: number; alertType?: string; limit?: number; offset?: number }): Promise<any> {
  const queryParams = new URLSearchParams()
  if (params.deviceId) queryParams.set('deviceId', String(params.deviceId))
  if (params.alertType) queryParams.set('alertType', params.alertType)
  if (params.limit) queryParams.set('limit', String(params.limit))
  if (params.offset) queryParams.set('offset', String(params.offset))
  return apiRequestProtected('GET', `/alert-history?${queryParams}`)
}

async function getAlertChartMarkers(deviceId: number, dateFrom?: string, dateTo?: string): Promise<any> {
  const queryParams = new URLSearchParams()
  if (dateFrom) queryParams.set('dateFrom', dateFrom)
  if (dateTo) queryParams.set('dateTo', dateTo)
  return apiRequestProtected('GET', `/alerts/chart-markers/${deviceId}?${queryParams}`)
}

async function getAccountAlertSettings(): Promise<any> {
  return apiRequestProtected('GET', '/account-alert-settings')
}

async function updateAccountAlertSettings(data: any): Promise<any> {
  return apiRequestProtected('PUT', '/account-alert-settings', data)
}

async function sendTestAlert(data: { type: 'email' | 'sms'; contact: string }): Promise<any> {
  return apiRequestProtected('POST', '/test-alert', data)
}

async function getMe(): Promise<{
  userId: number
  email: string
  firstName: string | null
  lastName: string | null
  role: 'admin' | 'partner' | 'customer' | 'guest'
  roleId: number
}> {
  return apiRequestProtected('GET', '/auth/me')
}

// --- Downlink remote-control (gates/valves) ---
async function getControlCapabilities(sigfoxId: string): Promise<any> {
  return apiRequestProtected('GET', `/devices/${sigfoxId}/control-capabilities`)
}

async function getDeviceCommands(sigfoxId: string): Promise<any> {
  return apiRequestProtected('GET', `/devices/${sigfoxId}/commands`)
}

async function createDeviceCommand(
  sigfoxId: string,
  payload: { targetValue: number; controlMode?: string; actuatorChannel?: number; ttlMinutes?: number }
): Promise<any> {
  return apiRequestProtected('POST', `/devices/${sigfoxId}/commands`, payload)
}

async function cancelDeviceCommand(sigfoxId: string, commandId: string): Promise<any> {
  return apiRequestProtected('DELETE', `/devices/${sigfoxId}/commands/${commandId}`)
}

export const api = {
  getVersion,
  getControlCapabilities,
  getDeviceCommands,
  createDeviceCommand,
  cancelDeviceCommand,
  healthcheck,
  healthcheckProtected,
  signIn,
  getMe,
  getDevices,
  getDevicesPage,
  getDeviceConfig,
  getSensorConfig,
  getUserUnits,
  getUnitConversions,
  updateUserUnitSettings,
  getReadings,
  hideReadings,
  unhideReadings,
  getLastReadings,
  getDevicesStatus,
  getBatteryReadings,
  getLatestBatteryReading,
  getSensorConfigs,
  getRawConfigMessages,
  getRawSigfoxMessages,
  getHysteresisSettings,
  updateHysteresisSettings,
  getAlertControls,
  updateAlertControls,
  getAlertSettings,
  createAlertSettings,
  updateAlertSettings,
  getSensorPosition,
  getSensorThresholds,
  setSensorThresholds,
  getSensorAlerts,
  setSensorAlerts,
  getAlertHistorySummary,
  getDeviceAlertStates,
  getAlertHistory,
  getAlertChartMarkers,
  getAccountAlertSettings,
  updateAccountAlertSettings,
  sendTestAlert,
}
